import React, { useState, useLayoutEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { 
  Menu, X, Settings, LogOut, Camera, Crown, Home, Dumbbell, Apple, 
  TrendingUp, Activity as ActivityIcon, Swords, Search, Users, 
  Timer, ChevronRight, Shield, GraduationCap 
} from 'lucide-react';
import UpgradeModal from './UpgradeModal';
import NotificationBell from './NotificationBell';

const HamburgerMenu = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [user, setUser] = useState(null);

  // Get user from localStorage on mount and when menu opens
  useLayoutEffect(() => {
    const loadUser = () => {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        try {
          setUser(JSON.parse(storedUser));
        } catch (e) {
          console.error('Error parsing user data');
          setUser(null);
        }
      }
    };
    loadUser();
  }, [showMobileMenu]); // Reload user data when menu opens

  // Close menu when route changes
  useLayoutEffect(() => {
    setShowMobileMenu(false);
  }, [location.pathname]);

  // Close menu when clicking outside
  useLayoutEffect(() => {
    const handleClickOutside = (event) => {
      if (showMobileMenu && !event.target.closest('.hamburger-menu-container')) {
        setShowMobileMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showMobileMenu]);

  const handleLogout = () => {
    // Clear all auth data from localStorage
    localStorage.removeItem('token');
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
    setShowMobileMenu(false);
    // Force page reload to reset React state and redirect to auth
    window.location.href = '/auth';
  };

  const menuItems = [
    { icon: Home, label: 'Main Hub', path: '/hub', color: 'text-blood-400' },
    { icon: Dumbbell, label: 'Workout Arena', path: '/dashboard', color: 'text-blood-400' },
    { icon: Timer, label: 'Active Recovery', path: '/active-recovery', color: 'text-blood-400' },
    { icon: Apple, label: 'Nutrition', path: '/nutrition', color: 'text-blood-400' },
    { icon: Swords, label: 'Battle Arena', path: '/battle-arena', color: 'text-blood-400' },
    { icon: Search, label: 'Find People', path: '/find-people', color: 'text-blood-400' },
    { icon: Users, label: 'Friends', path: '/friends', color: 'text-blood-400' },
    { icon: Home, label: 'Family', path: '/family', color: 'text-blood-400' },
    { icon: GraduationCap, label: 'Teams & Coaching', path: '/teams', color: 'text-amber-400' },
    { icon: TrendingUp, label: 'Progress', path: '/progress', color: 'text-blood-400' },
    { icon: ActivityIcon, label: 'Activity', path: '/activity', color: 'text-blood-400' },
    { icon: Camera, label: 'Progress Photos', path: '/progress-photos', color: 'text-blood-400' },
    { icon: Shield, label: 'Armory', path: '/armory', color: 'text-amber-400' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <>
      {/* Fixed Header Bar */}
      <div className="fixed top-0 left-0 right-0 z-40 glass border-b border-stone-800 px-4 py-2 safe-area-top">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <button
            onClick={() => navigate('/hub')}
            className="text-blood-400 font-bold text-lg tracking-wider"
          >
            ANVIL & ASH
          </button>
          
          <div className="flex items-center gap-1">
            {user?.is_org_staff && (
              (() => {
                const inCoaching = location.pathname.startsWith('/teams');
                return (
                  <button
                    onClick={() => navigate(inCoaching ? '/hub' : '/teams')}
                    className={`flex items-center gap-1.5 px-3 py-1.5 mr-1 rounded-full text-xs font-bold tracking-wide transition-all border ${
                      inCoaching
                        ? 'bg-blood-950/40 border-blood-800 text-blood-300 hover:bg-blood-900/50'
                        : 'bg-amber-950/40 border-amber-700 text-amber-300 hover:bg-amber-900/50'
                    }`}
                    data-testid="role-toggle-btn"
                    title={inCoaching ? 'Switch to your personal training' : 'Switch to your coach dashboard'}
                  >
                    {inCoaching ? <Dumbbell className="w-3.5 h-3.5" /> : <GraduationCap className="w-3.5 h-3.5" />}
                    <span className="hidden sm:inline">{inCoaching ? 'My Training' : 'Coaching'}</span>
                  </button>
                );
              })()
            )}
            <NotificationBell />
            <div className="hamburger-menu-container relative">
            <Button
              variant="ghost"
              onClick={() => setShowMobileMenu(!showMobileMenu)}
              className="p-2 text-blood-400 hover:bg-blood-950/50"
              data-testid="hamburger-menu-btn"
            >
              {showMobileMenu ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </Button>

            {/* Dropdown Menu */}
            {showMobileMenu && (
              <div 
                className="absolute right-0 top-12 glass border border-stone-700 rounded-lg overflow-hidden min-w-[260px] max-h-[calc(100vh-100px)] overflow-y-auto shadow-2xl"
                style={{ maxHeight: 'calc(100vh - 80px)' }}
              >
                {/* User Info */}
                {user && (
                  <div className="px-4 py-3 border-b border-stone-700 bg-stone-900/50">
                    <div className="font-bold text-blood-400">{user.warrior_name || user.username || user.email?.split('@')[0]}</div>
                    <div className="text-xs text-stone-400">{user.tier === 'premium' ? 'Premium Member' : 'Free Tier'}</div>
                  </div>
                )}

                {/* Menu Items */}
                <div className="py-2">
                  {menuItems.map((item) => (
                    <button
                      key={item.path}
                      onClick={() => {
                        navigate(item.path);
                        setShowMobileMenu(false);
                      }}
                      className={`w-full flex items-center justify-between px-4 py-3 hover:bg-blood-950/50 transition-colors ${
                        isActive(item.path) ? 'bg-blood-950/50 border-l-2 border-blood-500' : ''
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <item.icon className={`w-5 h-5 ${item.color}`} />
                        <span className={isActive(item.path) ? 'text-blood-400 font-medium' : 'text-stone-300'}>
                          {item.label}
                        </span>
                      </div>
                      {isActive(item.path) && <ChevronRight className="w-4 h-4 text-blood-400" />}
                    </button>
                  ))}

                  {/* Admin Panel - Only for admins */}
                  {user?.is_admin && (
                    <button
                      onClick={() => {
                        navigate('/admin');
                        setShowMobileMenu(false);
                      }}
                      className={`w-full flex items-center justify-between px-4 py-3 hover:bg-blood-950/50 transition-colors ${
                        isActive('/admin') ? 'bg-blood-950/50 border-l-2 border-blood-500' : ''
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <Crown className="w-5 h-5 text-yellow-400" />
                        <span className={isActive('/admin') ? 'text-yellow-400 font-medium' : 'text-yellow-400'}>
                          Admin Panel
                        </span>
                      </div>
                    </button>
                  )}
                </div>

                {/* Divider */}
                <div className="border-t border-stone-700" />

                {/* Upgrade & Settings */}
                <div className="py-2">
                  {user?.tier !== 'premium' && (
                    <button
                      onClick={() => {
                        setShowUpgradeModal(true);
                        setShowMobileMenu(false);
                      }}
                      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-yellow-950/30 transition-colors"
                    >
                      <Crown className="w-5 h-5 text-yellow-400" />
                      <span className="text-yellow-400 font-medium">Upgrade to Premium</span>
                    </button>
                  )}
                  
                  <button
                    onClick={() => {
                      navigate('/settings');
                      setShowMobileMenu(false);
                    }}
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-stone-800/50 transition-colors"
                  >
                    <Settings className="w-5 h-5 text-stone-400" />
                    <span className="text-stone-400">Settings</span>
                  </button>
                  
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-blood-950/50 transition-colors"
                  >
                    <LogOut className="w-5 h-5 text-blood-400" />
                    <span className="text-blood-400">Logout</span>
                  </button>
                </div>
              </div>
            )}
            </div>
          </div>
        </div>
      </div>

      {/* Spacer for fixed header */}
      <div className="h-14" />

      {/* Overlay when menu is open */}
      {showMobileMenu && (
        <div 
          className="fixed inset-0 bg-black/50 z-30"
          onClick={() => setShowMobileMenu(false)}
        />
      )}

      {showUpgradeModal && <UpgradeModal onClose={() => setShowUpgradeModal(false)} />}
    </>
  );
};

export default HamburgerMenu;
